import { Injectable, signal, computed } from '@angular/core';

export interface BreadcrumbItem {
  label: string;
  route?: string;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  // The current breadcrumb trail
  private readonly _breadcrumbs = signal<BreadcrumbItem[]>([]);

  // The page context (e.g., search query, pipeline name)
  private readonly _pageContext = signal<string | null>(null);

  // Public readonly signals
  readonly breadcrumbs = this._breadcrumbs.asReadonly();
  readonly pageContext = this._pageContext.asReadonly();

  // Computed: Get the display title for the header
  readonly headerTitle = computed(() => {
    const context = this._pageContext();
    if (context) {
      return context;
    }
    return 'Ubiquity';
  });

  // Computed: Check if we have a context set
  readonly hasContext = computed(() => this._pageContext() !== null);

  /**
   * Set the page context (search query, pipeline name, etc.)
   * This is displayed prominently in the header
   */
  setPageContext(context: string | null): void {
    this._pageContext.set(context);
  }

  /**
   * Set the breadcrumb trail
   */
  setBreadcrumbs(items: BreadcrumbItem[]): void {
    this._breadcrumbs.set(items);
  }

  /**
   * Clear the page context (returns to default "Ubiquity")
   */
  clearContext(): void {
    this._pageContext.set(null);
    this._breadcrumbs.set([]);
  }

  /**
   * Set context for a search query
   */
  setSearchContext(query: string): void {
    this._pageContext.set(query);
    this._breadcrumbs.set([
      { label: 'Search', route: '/search' },
      { label: query }
    ]);
  }

  /**
   * Set context for a pipeline
   */
  setPipelineContext(pipelineName: string, pipelineId?: string): void {
    this._pageContext.set(pipelineName);
    this._breadcrumbs.set([
      { label: 'Pipelines', route: '/pipelines' },
      { label: pipelineName, route: pipelineId ? `/search-results/${pipelineId}` : undefined }
    ]);
  }

  /**
   * Set context for a saved search
   */
  setSavedSearchContext(searchName: string, searchId?: string): void {
    this._pageContext.set(searchName);
    this._breadcrumbs.set([
      { label: 'Search', route: '/search' },
      { label: searchName, route: searchId ? `/search-results/${searchId}` : undefined }
    ]);
  }
}
